import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppSelector } from "../../app/hooks";
import { getParamAsInt } from "../../app/utils";
import { UserRole } from "../../app/userSlice";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import { ClassLinkOrCleverIconWithTooltip } from "../../components/ClassLinkOrCleverIcon";
import { Loading } from "../../components/Loading";
import SchoolSettingsSection from "./schoolSettings/SchoolSettingsSection";
import SchoolInfoSection from "./SchoolInfoSection";
import SchoolCoursesSection from "./SchoolCoursesSection";
import SchoolAdminsSection from "./SchoolAdminsSection";
import { SchoolSpaViewModel, useLazyGetSchoolInfoQuery } from "./schoolsApiSlice";
import SchoolRosterSection from "./SchoolRosterSection";

export default function EditSchool() {
	const [queryParams] = useSearchParams();
	const schoolId = getParamAsInt(queryParams, "schoolId");

	const { role } = useAppSelector((state) => state.user);
	const { config } = useAppSelector((state) => state.site);

	const [errorMessage, setErrorMessage] = useState("");
	const [showOutsideIcon, setShowOutsideIcon] = useState(false);

	const [schoolInfo, setSchoolInfo] = useState<SchoolSpaViewModel>();

	const [getSchoolInfo, getSchoolInfoResult] = useLazyGetSchoolInfoQuery();

	useEffect(() => {
		if (schoolId) {
			getSchoolInfo(schoolId)
				.unwrap()
				.then((payload) => {
					setSchoolInfo(payload);
				});
		}
	}, []);

	useEffect(() => {
		setShowOutsideIcon(!!schoolInfo?.classLinkID || !!schoolInfo?.cleverID);
	}, [schoolInfo]);

	const tooltipContent = schoolInfo?.cleverID
		? "This school has a Clever account"
		: schoolInfo?.classLinkID
			? "This school has a ClassLink account"
			: undefined;

	return (
		<section>
			<h1>Manage School</h1>
			<Loading loading={getSchoolInfoResult.isLoading} />
			{schoolInfo && schoolId && (
				<section>
					<div className="flex mt-6 gap-x-3 items-center">
						{showOutsideIcon && (
							<ClassLinkOrCleverIconWithTooltip
								isClassLinkEntity={!!schoolInfo.classLinkID}
								isCleverEntity={!!schoolInfo.cleverID}
								iconClasses="size-10"
								tooltipId="outside-site-icon-tooltip"
								tooltipContent={tooltipContent}
							/>
						)}
						<h2>{schoolInfo.name}</h2>
					</div>
					<AlertMessage
						className="mt-4 text-sm"
						type={AlertLevel.Error}
						message={errorMessage}
						showIcon={true}
					/>
					{!config?.isUtahComposeSite && (
						<SchoolSettingsSection
							schoolInfo={schoolInfo}
							setSchoolInfo={setSchoolInfo}
							setErrorMessage={setErrorMessage}
						/>
					)}
					{role !== UserRole.SchoolAdmin && (
						<SchoolInfoSection
							schoolInfo={schoolInfo}
							addingNew={false}
							setSchoolInfo={setSchoolInfo}
						/>
					)}
					<SchoolCoursesSection schoolId={schoolId} />
					{role !== UserRole.SchoolAdmin && <SchoolAdminsSection schoolId={schoolId} />}
					{schoolInfo.showRosterSection && <SchoolRosterSection />}
				</section>
			)}
		</section>
	);
}
